import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "fgm_cart_v1";

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [open, setOpen] = useState(false);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch (err) {
            console.warn("[FGM] cart persist failed:", err);
        }
    }, [items]);

    const addItem = (product, size, color) => {
        setItems((prev) => {
            const idx = prev.findIndex(
                (i) =>
                    i.product_id === product.id &&
                    i.size === size &&
                    (i.color || null) === (color || null),
            );
            if (idx >= 0) {
                const next = [...prev];
                next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
                return next;
            }
            return [
                ...prev,
                {
                    product_id: product.id,
                    name: product.name,
                    price: product.price,
                    image: product.image,
                    size,
                    color: color || null,
                    quantity: 1,
                },
            ];
        });
        setOpen(true);
    };

    const removeItem = (product_id, size, color = null) => {
        setItems((prev) =>
            prev.filter(
                (i) =>
                    !(
                        i.product_id === product_id &&
                        i.size === size &&
                        (i.color || null) === (color || null)
                    ),
            ),
        );
    };

    const updateQuantity = (product_id, size, quantity, color = null) => {
        setItems((prev) =>
            prev
                .map((i) =>
                    i.product_id === product_id &&
                    i.size === size &&
                    (i.color || null) === (color || null)
                        ? { ...i, quantity: Math.max(1, Math.min(20, quantity)) }
                        : i,
                ),
        );
    };

    const clear = () => setItems([]);

    const subtotal = useMemo(
        () => items.reduce((sum, i) => sum + i.price * i.quantity, 0),
        [items],
    );
    const count = useMemo(
        () => items.reduce((sum, i) => sum + i.quantity, 0),
        [items],
    );

    const value = {
        items,
        addItem,
        removeItem,
        updateQuantity,
        clear,
        subtotal,
        count,
        open,
        setOpen,
    };

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
}
